import type { ReactElement, ReactNode } from 'react';
import type { IconProps } from '../icons/generated';
import type { InputProps } from './Input';
import type { NumericInputBaseType, NumericInputProps } from './NumericInput';
import type { ChipInputProps, ChipInputValue } from './ChipInput';

export interface OldInputBaseProps {
  /** @deprecated leftIcon을 대신 사용해주세요. */
  startElement?: ReactElement<IconProps> | ReactNode;
  /** @deprecated rightIcon을 대신 사용해주세요. */
  endElement?: ReactElement<IconProps> | ReactNode;
  /** @deprecated hasCounter를 대신 사용해주세요. */
  showCounter?: boolean;
  /** @deprecated hasStatusIcon을 대신 사용해주세요. */
  showStatusIcon?: boolean;
}

export type OldInputProps = Omit<InputProps, 'leftIcon' | 'rightIcon' | 'hasCounter' | 'hasStatusIcon'> &
  OldInputBaseProps;

export function convertOldInputProps(props: OldInputProps): InputProps {
  const { startElement, endElement, showCounter, showStatusIcon, ...restProps } = props;

  return {
    ...restProps,
    leftIcon: startElement,
    rightIcon: endElement,
    hasCounter: showCounter,
    hasStatusIcon: showStatusIcon,
  };
}

export type OldNumericInputProps<TAllowEmpty extends boolean = false> = Omit<
  NumericInputProps<TAllowEmpty>,
  'leftIcon' | 'rightIcon' | 'hasStatusIcon'
> &
  Omit<OldInputBaseProps, 'showCounter'>;

export type OldNumericInputBaseType<TAllowEmpty extends boolean = false> = Omit<
  NumericInputBaseType<TAllowEmpty>,
  'leftIcon' | 'rightIcon' | 'hasStatusIcon'
> &
  Omit<OldInputBaseProps, 'showCounter'>;

export function convertOldNumericInputProps<TAllowEmpty extends boolean = false>(
  props: OldNumericInputProps<TAllowEmpty>,
): NumericInputProps<TAllowEmpty> {
  const { startElement, endElement, showStatusIcon, ...restProps } = props;

  return {
    ...restProps,
    leftIcon: startElement,
    rightIcon: endElement,
    hasStatusIcon: showStatusIcon,
  };
}

export interface OldChipInputValue extends Omit<ChipInputValue, 'deletable'> {
  /** @deprecated deletable을 대신 사용해주세요. */
  canDelete?: boolean;
}

export type OldChipInputProps = Omit<ChipInputProps, 'value' | 'onChange' | 'zIndex'> & {
  value?: OldChipInputValue[];
  /** @deprecated zIndex를 대신 사용해주세요. */
  z_index?: number;
  onChange?: (value: OldChipInputValue[]) => void;
};

function convertOldChipInputValue(value: OldChipInputValue): ChipInputValue {
  const { canDelete, ...restValue } = value;

  return {
    ...restValue,
    deletable: canDelete,
  };
}

function convertNewChipInputValue(value: ChipInputValue): OldChipInputValue {
  const { deletable, ...restValue } = value;

  return {
    ...restValue,
    canDelete: deletable,
  };
}

export function convertOldChipInputProps(props: OldChipInputProps): ChipInputProps {
  const { value, z_index, onChange, ...restProps } = props;

  return {
    ...restProps,
    value: value?.map(convertOldChipInputValue),
    zIndex: z_index,
    onChange: onChange ? (newValue) => onChange(newValue.map(convertNewChipInputValue)) : undefined,
  };
}
